import { inject } from '@angular/core';
import { Route, UrlSegment } from '@angular/router';
import {
  PermissionGrants,
  SiteSubmission,
  StudySubmission,
} from '@shared/interfaces/permissions.interface';
import { PermissionsService } from '@shared/services/permissions.service';
import { Observable, of, take, timeout, catchError } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import environment from '@environments/environment';
import {
  FEATURE_FLAGS_SIGNAL_STORE,
  FeatureFlag,
} from '@data-access/system-controls/feature-flags';

declare type ObservableMatchFn = (
  route: Route,
  segments: UrlSegment[],
) => Observable<boolean>;

export const canMatchSite: ObservableMatchFn = (
  _: Route,
  segments: UrlSegment[],
) => {
  return inject(PermissionsService)
    .observeEntityPermissions(
      segments[0].path,
      [SiteSubmission],
      [PermissionGrants.CanView],
    )
    .pipe(take(1));
};

export const canMatchStudy: ObservableMatchFn = (
  _: Route,
  segments: UrlSegment[],
) => {
  return inject(PermissionsService)
    .observeEntityPermissions(
      segments[0].path,
      [StudySubmission],
      [PermissionGrants.CanView],
    )
    .pipe(take(1));
};

// TODO: depricate this soon
export const canMatchAdminRole: (role: string) => ObservableMatchFn = (
  role: string,
) => {
  return (_: Route, __: UrlSegment[]) =>
    inject(PermissionsService).hasRole(role).pipe(take(1));
};

export const canMatchInternalUser: ObservableMatchFn = (
  _: Route,
  __: UrlSegment[],
) => {
  return inject(PermissionsService).isInternal$.pipe(take(1));
};

export const canMatchFeatureFlag: (ff: FeatureFlag) => ObservableMatchFn = (
  ff: FeatureFlag,
) => {
  return (_: Route, __: UrlSegment[]) => {
    const store = inject(FEATURE_FLAGS_SIGNAL_STORE);

    // Wait for feature flags to be ready (with 2s timeout)
    return toObservable(store.$isReady).pipe(
      filter((ready) => ready),
      timeout(2000),
      map(() => {
        // Check if flag is enabled in store or environment
        return store.$selectFeatureFlag(ff)();
      }),
      catchError(() => {
        // On timeout or error, fallback to environment then false
        console.warn(
          `[FeatureFlags] Timeout waiting for flags, falling back to environment for ${ff}`,
        );
        const envValue = (environment as any)[ff];
        return of(envValue ?? false);
      }),
      take(1),
    );
  };
};
